// src/hooks/useMerchant.js
import { useState, useEffect, useRef } from "react"
import { MerchantsApi } from "../Apis/merchantsApi"
import { Store } from "../Apis/storeApis"
import { message } from "antd"
import { translations } from "../utils/Translation/staticTranslation"

const mergeCountryCodes = (current = [], incoming = []) =>
  [...new Set([...current, ...incoming].map((code) => String(code ?? "").trim().toUpperCase()).filter(Boolean))]

const getStoreNameFromMerchant = (merchant = {}) => {
  const storeNames = Array.isArray(merchant?.store_names) ? merchant.store_names : []
  const firstStoreName = storeNames[0]

  return {
    store_name_display_en: firstStoreName || "—",
    store_name_display_ar: firstStoreName || "—",
  }
}

const useMerchant = (
  selectedStatus,
  selectedCategory,
  searchTerm,
  filterStatus,
  filterCountry,
  filterIndustry,
  filterHasStore,
  handleDeleteConfirmationModalClose
) => {
  const [loading, setLoading] = useState(false)
  const [merchants, setMerchants] = useState([])
  const [merchantCountries, setMerchantCountries] = useState([])
  const [updateLoading, setUpdateLoading] = useState({})
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [sortConfig, setSortConfig] = useState(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    next: null,
    previous: null,
  })

  const lang = localStorage.getItem("language") || "ar"
  const merchantPage = translations[lang]?.productPage
  const { common_error_msg } = translations[lang]

  // Prevent duplicate API calls
  const fetchInProgress = useRef(false)
  const hasFetchedInitial = useRef(false)
  const prevParamsRef = useRef(null)
  const getOrderingValue = (sortOverride = null) => {
    const activeSort = sortOverride || sortConfig
    if (!activeSort?.key) return ""
    return activeSort.direction === "desc"
      ? `-${activeSort.key}`
      : activeSort.key
  }
  const getOrderByValue = (sortOverride = null) => {
    const activeSort = sortOverride || sortConfig
    if (!activeSort?.key) return ""
    return activeSort.direction === "desc" ? "desc" : "asc"
  }

  useEffect(() => {
    // Only fetch once on mount
    if (!hasFetchedInitial.current && !fetchInProgress.current) {
      hasFetchedInitial.current = true
      fetchApprovedMerchants(
        "",
        "",
        "",
        1,
        filterCountry,
        filterIndustry,
        filterHasStore
      )
    }
  }, [])

  useEffect(() => {
    // Only trigger search if we have a category or search term
    if (selectedCategory || searchTerm) {
      const handler = setTimeout(() => {
        if (selectedStatus === "approved") {
          // Reset to page 1 when searching/filtering
          setPagination((prev) => ({ ...prev, current: 1 }))
          fetchApprovedMerchants(
            selectedCategory,
            searchTerm,
            filterStatus,
            1,
            filterCountry,
            filterIndustry,
            filterHasStore
          )
        } else if (selectedStatus === "blocked") {
          setPagination((prev) => ({ ...prev, current: 1 }))
          fetchMerchantsRequests([6], selectedCategory, searchTerm, 1)
        } else {
          // pending | rejected only — never map "blocked" to status 3
          setPagination((prev) => ({ ...prev, current: 1 }))
          const requestStatus = selectedStatus === "pending" ? [0, 1] : [3]
          fetchMerchantsRequests(requestStatus, selectedCategory, searchTerm, 1)
        }
      }, 500) // Reduced debounce time

      return () => clearTimeout(handler)
    }
  }, [
    searchTerm,
    filterStatus,
    selectedStatus,
    selectedCategory,
    filterCountry,
    filterIndustry,
    filterHasStore,
  ])

  const fetchApprovedMerchants = async (
    category = "",
    search = "",
    status = "",
    page = 1,
    country = "",
    industry = "",
    hasStore = "",
    sortOverride = null
  ) => {
    // Prevent duplicate calls
    const currentParams = JSON.stringify({
      category,
      search,
      status,
      page,
      country,
      industry,
      hasStore,
      sortConfig: sortOverride || sortConfig,
    })

    if (fetchInProgress.current || prevParamsRef.current === currentParams) {
      return
    }

    fetchInProgress.current = true
    prevParamsRef.current = currentParams
    setLoading(true)

    const payload = {}

    if (category === "Vendor" && search) {
      payload.vendor_name = search
    } else if (category === "Business" && search) {
      payload.business = search
    }

    if (status) {
      payload.status = status
    }

    if (country) {
      payload.country = country
    }

    if (industry) {
      payload.industry = industry
    }

    if (hasStore === "with_store" || hasStore === "without_store") {
      payload.has_store = hasStore
    }
    const ordering = getOrderingValue(sortOverride)
    const orderBy = getOrderByValue(sortOverride)
    if (ordering) {
      payload.ordering = ordering
      payload.order_by = orderBy
    }

    // Add page parameter
    if (page) {
      payload.page = page
    }

    try {
      const response = await MerchantsApi.listApprovedMerchants(payload)
      if (response.status === 200) {
        // Fix: Extract results from paginated response
        const data = response.data
        const rows = data.results || data || []
        const normalizedRows = Array.isArray(rows)
          ? rows.map((merchant) => ({
              ...merchant,
              name_english:
                merchant.name_english || merchant.name_arabic || "—",
              name_arabic:
                merchant.name_arabic || merchant.name_english || "—",
              vendor_name:
                merchant.vendor_name || merchant.vendor_name_arabic || "—",
              vendor_name_arabic:
                merchant.vendor_name_arabic || merchant.vendor_name || "—",
              ...getStoreNameFromMerchant(merchant),
            }))
          : []
        setMerchants(normalizedRows)

        if (Array.isArray(data.merchant_countries)) {
          // Keep a stable list of countries across filtered calls
          // so selecting one country doesn't collapse dropdown options.
          setMerchantCountries((prev) =>
            mergeCountryCodes(prev, data.merchant_countries)
          )
        }

        // Update pagination state
        setPagination({
          current: page,
          pageSize: 10,
          total: data.count || 0,
          next: data.next || null,
          previous: data.previous || null,
        })
      }
      return response
    } catch (error) {
      console.error("Error fetching approved merchants:", error)
      prevParamsRef.current = null // Reset on error to allow retry
      throw error
    } finally {
      setLoading(false)
      fetchInProgress.current = false
    }
  }

  const fetchMerchantsRequests = async (
    type = [0, 1],
    category = "",
    search = "",
    page = 1,
    sortOverride = null
  ) => {
    setLoading(true)

    const payload = {
      request_status: type,
      request_type: "Register Tennant",
    }

    if (category === "Vendor" && search) {
      payload.vendor_name = search
    } else if (category === "Business" && search) {
      payload.business_name = search
    }
    const ordering = getOrderingValue(sortOverride)
    const orderBy = getOrderByValue(sortOverride)
    if (ordering) {
      payload.ordering = ordering
      payload.order_by = orderBy
    }

    // Add page parameter
    if (page) {
      payload.page = page
    }

    try {
      const response = await Store.getOtherStores(payload)
      if (response.status === 200) {
        const data = response.data
        // Fix: Extract results from paginated response
        setMerchants(data.results || data || [])
        
        // Update pagination state if paginated response
        if (data.count !== undefined) {
          setPagination({
            current: page,
            pageSize: 10,
            total: data.count || 0,
            next: data.next || null,
            previous: data.previous || null,
          })
        }
      }
      return response
    } catch (error) {
      console.error("Error fetching merchant requests:", error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const handleMerchantUpdate = async (row, status) => {
    const payload = { active_status: status }
    const merchantId = row?.id
    setUpdateLoading((prev) => ({ ...prev, [merchantId]: true }))
    try {
      const response = await MerchantsApi.updateBusiness(payload, row.id)
      if (response.status === 200) {
        setMerchants((prev) =>
          prev.map((merchant) =>
            merchant.id === row.id
              ? { ...merchant, active_status: status }
              : merchant
          )
        )
      }
      return response
    } catch (error) {
      console.error("Error updating merchant status:", error)
      throw error
    } finally {
      setUpdateLoading((prev) => ({ ...prev, [merchantId]: false }))
    }
  }

  const handleDelete = async (storeId) => {
    if (!storeId) {
      message.error(merchantPage?.store_select_error)
      return
    }
    setDeleteLoading(true)
    try {
      const response = await Store.deleteRequest(storeId)
      if (response.status === 200) {
        message.success(common_error_msg?.rejected_request_deleted_successfully)
        setMerchants((prev) => prev.filter((store) => store.id !== storeId))
      }
      handleDeleteConfirmationModalClose()
      return response
    } catch (error) {
      message.error(common_error_msg?.failed_to_delete_store)
      console.error("Error deleting store:", error)
      throw error
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleApprovedBusinessDelete = async (businessId) => {
    if (!businessId) {
      message.error(merchantPage?.store_select_errorx)
      return
    }
    setDeleteLoading(true)
    try {
      const response = await MerchantsApi.deleteBusiness(businessId)
      if (response.status === 200) {
        message.success(common_error_msg?.business_deleted_successfully)
        handleDeleteConfirmationModalClose()
        setMerchants((prev) =>
          prev.filter((business) => business.id !== businessId)
        )
      }
      return response
    } catch (error) {
      message.error(common_error_msg?.failed_to_delete_business)
      console.error("Error deleting business:", error)
      throw error
    } finally {
      setDeleteLoading(false)
    }
  }

  return {
    updateLoading,
    handleApprovedBusinessDelete,
    setMerchants,
    loading,
    merchants,
    merchantCountries,
    fetchApprovedMerchants,
    fetchMerchantsRequests,
    handleMerchantUpdate,
    handleDelete,
    deleteLoading,
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
  }
}

export default useMerchant
